/**
 * Total observed online hours, and the two states in which it prints no number.
 *
 * The two states are the point of the file:
 *
 *  - **`covered === null`** — the page did not fully cover a single date, so there
 *    is no window to state and therefore no total. `web/shapes/overview.md` §8: a
 *    partial sum labelled as a total is the mistake; a narrowed window is exact.
 *  - **`onlineSeconds === 0` with rows present** — "chưa quan sát được giờ nào",
 *    which is not `0 g`. Task 42 established the rule one level down (an hour that
 *    was never computed is not an hour with no data) and Task 43 found it violated
 *    in shipped code. `formatDurationSeconds` is documented to render zero as
 *    `0 ph`, so the branch that would reach it with an absence is simply not written.
 */

import type { ReactNode } from 'react';

import { useI18n } from '../../i18n';
import { formatDurationSeconds } from '../../timer/elapsed';
import type { WindowFold } from '../../overview/fold';
import { CompletenessNote, TruncationNote, hasGaps } from './CompletenessNote';
import { EmptyState } from '../../states/EmptyState';
import { Tile } from './Tile';

export function HoursTile({
  fold,
  limit,
}: {
  readonly fold: WindowFold;
  readonly limit: number;
}): ReactNode {
  const { t } = useI18n();
  const labels = { hour: t('common.hourShort'), minute: t('common.minuteShort') };

  if (fold.covered === null) {
    // The shared empty state rather than a sentence set where a number goes: a tile
    // with no figure is one of the nine states, not a typographic special case
    // (Luật Chín Trạng Thái; finish review, fix 2).
    return (
      <Tile
        title={t('overview.hours.title')}
        figure={
          <EmptyState
            title={t('overview.nothingCovered')}
            description={t('overview.hours.uncovered.next')}
            testId="k4-hours-uncovered"
          />
        }
        testId="k4-hours"
      />
    );
  }

  const measured = fold.totals.onlineSeconds > 0;

  return (
    <Tile
      title={t('overview.hours.title')}
      figure={
        measured ? (
          <span className="wp-figure">
            {formatDurationSeconds(fold.totals.onlineSeconds, labels)}
          </span>
        ) : (
          <EmptyState
            title={t('overview.hours.none')}
            description={t('overview.hours.none.next')}
            testId="k4-hours-none"
          />
        )
      }
      detail={
        <>
          <p className="wp-small">
            {t('overview.range', { from: fold.covered.from, to: fold.covered.to })}
          </p>
          <p className="wp-small wp-muted">
            {t('overview.hours.over', {
              days: fold.perDay.length,
              rows: fold.totals.rows,
              employees: fold.employeeCount,
            })}
          </p>
          {measured ? (
            <p className="wp-small" data-testid="k4-hours-active">
              {t('overview.hours.active', {
                value: formatDurationSeconds(fold.totals.activeSeconds, labels),
              })}
            </p>
          ) : null}
        </>
      }
      caveat={
        <>
          <CompletenessNote fold={fold} testId="k4-hours" />
          <TruncationNote fold={fold} limit={limit} testId="k4-hours-truncated" />
        </>
      }
      // Gaps are a thing to act on; an unmeasured window is an empty tile, and an
      // empty tile is paper (Luật Màu Có Nghĩa; finish review, round 2, fix 2).
      attention={hasGaps(fold)}
      testId="k4-hours"
    />
  );
}
